/**
 * The click-to-accept step every signed-in client meets (Lloyd, Jul 31: "a 'click to accept' button
 * on his portal that all clients will be confronted with before moving forward").
 *
 * Two consents, presented together but answered separately:
 *  - Terms of representation is required, so the portal stays blocked until it is accepted.
 *  - Sharing with a referral partner is optional. A client may decline and carry on using the
 *    portal normally; declining simply means their case cannot be shown to a partner. Bundling the
 *    two would make "consent" meaningless, because refusing would cost them access.
 *
 * Renders nothing at all when there is nothing outstanding, so it costs one request per session and
 * is invisible to a client who has already answered.
 */

import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Typography,
} from '@mui/material';

/** Twin of ConsentType in backend/src/config/consentDocuments.ts. Change both together. */
type ConsentType =
  | 'third-party-sharing'
  | 'terms-of-representation'
  | 'text-messaging';

interface ConsentStatus {
  type: ConsentType;
  title: string;
  summary: string;
  body: string;
  required: boolean;
  granted: boolean;
  outdated: boolean;
}

const ConsentGate: React.FC = () => {
  const [consents, setConsents] = useState<ConsentStatus[] | null>(null);
  const [submitting, setSubmitting] = useState<ConsentType | null>(null);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async (): Promise<void> => {
    try {
      const response = await fetch(`${process.env.API_HOST || ''}/api/v1/user/consents`, {
        credentials: 'include',
      });
      if (!response.ok) {
        // A client who isn't signed in has nothing to answer — stay silent rather than showing an
        // error over a page that is working perfectly well.
        setConsents([]);
        return;
      }
      const data = await response.json();
      setConsents(data.consents ?? []);
    } catch {
      setConsents([]);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const answer = async (type: ConsentType, granted: boolean): Promise<void> => {
    setSubmitting(type);
    setError(null);
    try {
      const response = await fetch(`${process.env.API_HOST || ''}/api/v1/user/consents`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ type, granted }),
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(data.message || 'Could not save your answer');
      setConsents(data.consents ?? []);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not save your answer');
    } finally {
      setSubmitting(null);
    }
  };

  if (!consents) return null;

  // Anything unanswered, or answered against wording that has since changed.
  const outstanding = consents.filter((c) => !c.granted);
  if (outstanding.length === 0) return null;

  const blocking = outstanding.some((c) => c.required);

  return (
    <Dialog
      open
      maxWidth="sm"
      fullWidth
      // A required consent cannot be dismissed; an optional one can be left for later.
      disableEscapeKeyDown={blocking}
      onClose={() => {
        if (!blocking) setConsents([]);
      }}
    >
      <DialogTitle>Before you continue</DialogTitle>
      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {outstanding.map((consent, index) => (
          <Box key={consent.type} sx={{ mb: 3 }}>
            {index > 0 && <Divider sx={{ mb: 3 }} />}
            <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
              {consent.title}
            </Typography>
            {consent.outdated && (
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                We have updated this since you last agreed, so please read it again.
              </Typography>
            )}
            <Typography variant="body2" sx={{ mt: 1, lineHeight: 1.6 }}>
              {consent.body}
            </Typography>

            <Box sx={{ mt: 2, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
              <Button
                variant="contained"
                disabled={submitting !== null}
                onClick={() => answer(consent.type, true)}
              >
                {submitting === consent.type ? (
                  <CircularProgress size={20} color="inherit" />
                ) : (
                  consent.summary
                )}
              </Button>
              {!consent.required && (
                <Button
                  variant="text"
                  disabled={submitting !== null}
                  onClick={() => answer(consent.type, false)}
                >
                  No, don't share my case
                </Button>
              )}
            </Box>
          </Box>
        ))}
      </DialogContent>
      {!blocking && (
        <DialogActions>
          <Button onClick={() => setConsents([])}>Decide later</Button>
        </DialogActions>
      )}
    </Dialog>
  );
};

export default ConsentGate;
